import React, { useEffect, useMemo, useRef, useState } from 'react';
import './index.css';
import {
  Box,
  Button,
  Paper,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material';
import Header from 'components/header';
import { borderRadiusValues } from 'assets/theme/theme';
import mobxStore from 'mobx/rootStore';
import Col from 'components/base/col';
import StatisticWidget from 'components/statisticWidget/statisticWidget';
import { observer } from 'mobx-react-lite';
import Row from 'components/base/row';
import CategoryItem from 'components/categoryItem';
import { chain, map, sortBy } from 'lodash';
import { Dictionary } from 'mobx/helpers/dictionary';
import Chart from 'components/chart/chart';
import { DtpCategoryStatItem } from 'mobx/models/dtpCategoryStatItem';
import { DisabledCategories } from 'mobx/helpers/disabledCategories';
import HsContainer from 'components/hsContainer';
import { useIsMobile } from 'hooks/useIsMobile';
import { urlPrefix } from 'api/api';
import ExpandContainer from 'components/expandContainer';
import { nbsp } from 'utils/constants';
import { useAnchorParams } from 'hooks/useAnchorParams';

export default observer(function App() {
  const { dataStore } = mobxStore;
  const theme = useTheme();

  const [disabledCategories] = useState(new DisabledCategories());

  useEffect(() => {
    if (!dataStore.dictionaries && !dataStore.isGeneralInfoPending) {
      dataStore.fetchGeneralInfo();
    }
  }, [dataStore]);

  useEffect(() => {
    if (!dataStore.statistic && !dataStore.isStatPending) {
      dataStore.fetchStatistic();
    }
  }, [dataStore]);

  useEffect(() => {
    if (!dataStore.chartData && !dataStore.isChartPending) {
      dataStore.fetchChart();
    }
  }, [dataStore]);

  const categories = dataStore.dictionaries?.categories;
  const dict = useMemo(
    () => (categories ? new Dictionary(categories) : null),
    [categories]
  );

  const statModels = useMemo(() => {
    if (dataStore.chartData && dict) {
      return sortBy(
        map(dataStore.chartData, (data) => new DtpCategoryStatItem(data, dict)),
        (item) => -item.lastMonthCount
      );
    }

    return null;
  }, [dataStore.chartData, dict]);

  const serieses = chain(statModels)
    .filter((model) => !disabledCategories.isDisabled(model.categoryId))
    .map((model) => model.asLineChartItem(theme))
    .value();

  const isMobile = useIsMobile();
  const { offset } = useAnchorParams();

  const mainPx = `${offset}px`;
  const mainPy = '62px';

  const innerPx = isMobile ? 5 : 15;
  const innerPy = isMobile ? 8 : 15;

  const borderRadius = isMobile
    ? borderRadiusValues.medium
    : borderRadiusValues.large;

  return (
    <Paper
      square
      sx={{
        'position': 'relative',
        'display': 'flex',
        'flexDirection': 'column',
        'alignItems': 'center',
        'flexGrow': 1,
        'px': mainPx,
        'py': mainPy,
        'bgcolor': 'transparent',
        'background':
          'linear-gradient(121.96deg, rgba(53, 53, 63, 0.8) 1.6%, rgba(20, 20, 27, 0.8) 89.55%)',
        '& video': {
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          width: 1,
          height: 1,
          zIndex: -1,
          objectFit: 'cover',
        },
      }}
    >
      <video
        ref={(el: null | HTMLVideoElement) => {
          if (el) {
            el.onload = () => {
              el.play();
            };
          }
        }}
        autoPlay
        playsInline
        muted
        loop
      >
        <source src={`${urlPrefix}/bgvideo.mp4`} type="video/mp4"></source>
      </video>

      <Col sx={{ gap: 8, flexGrow: 1, width: 1 }}>
        <Header></Header>

        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            py: innerPy,
            borderRadius,

            overflow: 'hidden',
            gap: 8,
          }}
        >
          <Col sx={{ gap: 4, px: innerPx }}>
            <Typography variant="f20">Показатели за текущий год</Typography>
            <Col>
              <Box
                sx={{
                  'display': 'flex',
                  'flexWrap': 'wrap',
                  'width': 1,
                  'gap': 3,
                  '& > *': {
                    flexGrow: 1,
                    flexBasis: 0,
                    flexShrink: 0,
                  },
                }}
              >
                {dataStore.statistic ? (
                  <>
                    <StatisticWidget
                      variant="light"
                      name={`Кол-во ДТП с${nbsp}пострадавшими`}
                      value={dataStore.statistic.count}
                    ></StatisticWidget>
                    <StatisticWidget
                      variant="dark"
                      name="Ранено"
                      value={dataStore.statistic.injured_count}
                      isSmallDigit
                    ></StatisticWidget>
                    <StatisticWidget
                      variant="dark"
                      name="Погибло"
                      value={dataStore.statistic.death_count}
                      isSmallDigit
                    ></StatisticWidget>
                  </>
                ) : (
                  <>
                    <Skeleton height={107}></Skeleton>
                    <Skeleton height={107}></Skeleton>
                    <Skeleton height={107}></Skeleton>
                  </>
                )}
              </Box>

              <ExpandContainer>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 3,
                    pt: 3,
                  }}
                >
                  {dataStore.statistic ? (
                    <>
                      <StatisticWidget
                        variant="dark"
                        name={`Cоциальный${nbsp}риск за${nbsp}2023${nbsp}год`}
                        infoText="Количество погибших в ДТП на 100 тыс. населения"
                        value={dataStore.statistic.social_risk}
                      ></StatisticWidget>
                      <StatisticWidget
                        variant="dark"
                        name={`Транспортный${nbsp}риск за${nbsp}2023${nbsp}год`}
                        infoText="Количество погибших на 10 тыс. ТС"
                        value={dataStore.statistic.transport_risk}
                      ></StatisticWidget>
                    </>
                  ) : (
                    <>
                      <Skeleton height={107}></Skeleton>
                      <Skeleton height={107}></Skeleton>
                    </>
                  )}
                </Box>
              </ExpandContainer>
            </Col>
          </Col>

          <Col sx={{ gap: 4, flexGrow: 1 }}>
            <Typography sx={{ px: innerPx }} variant="f20">
              Распределение ДТП по типам
            </Typography>
            <HsContainer>
              <Row
                sx={{
                  px: innerPx,
                  gap: 2.5,
                  justifyContent: 'space-between',
                }}
              >
                {dict ? (
                  <>
                    {map(statModels, (model) => {
                      const color =
                        theme.palette.custom.dtpTypes[model.categoryId];

                      if (theme.palette.custom.dtpTypes[model.categoryId]) {
                        return (
                          <CategoryItem
                            onClick={() => {
                              if (
                                serieses.length === 1 &&
                                !disabledCategories.isDisabled(model.categoryId)
                              ) {
                                return;
                              }
                              disabledCategories.toggle(model.categoryId);
                            }}
                            isDisabled={disabledCategories.isDisabled(
                              model.categoryId
                            )}
                            key={model.categoryId}
                            color={color}
                            name={dict.find(model.categoryId)?.name || ''}
                          ></CategoryItem>
                        );
                      }
                      return null;
                    })}
                  </>
                ) : (
                  ''
                )}
              </Row>
            </HsContainer>

            {serieses.length ? (
              <Chart sx={{ px: innerPx }} serieses={serieses}></Chart>
            ) : (
              ''
            )}
          </Col>
          <Row sx={{ px: innerPx, width: 1, justifyContent: 'center' }}>
            <Button
              variant="contained"
              component="a"
              target="_blank"
              sx={{
                px: 4.5,
                fontSize: isMobile ? '18px !important' : '20px !important',
                borderRadius: 100,
                // Для тильды
                color: `${theme.palette.text.primary} !important`,
                textDecoration: 'none !important',
                bgcolor: `${theme.palette.primary.main} !important`,
                boxShadow: 'none !important',
              }}
              href={process.env.REACT_APP_BUTTON_URL}
            >
              Смотреть ДТП на карте
            </Button>
          </Row>
        </Paper>
      </Col>
    </Paper>
  );
});
